'use client';
import { useCallback, useMemo, useRef } from 'react';
import { useQueries } from '@tanstack/react-query';
import { loadPluginUi, pluginUiMayMount } from './pluginUi';
import { usePluginUi } from './queries';
import { useTranslation } from './i18n';
import { useMobileViewport } from './useMobile';
import type { PluginUiListing } from './types';
import type { LoadedPluginDockSource } from './operationDockPlugins';

/** Discover only declared, authorized sources through the existing immutable bundle loader. */
export function usePluginDockSources() {
  const { locale } = useTranslation();
  const mobile = useMobileViewport();
  const listing = usePluginUi(locale);
  const entries = useMemo(() => (listing.data ?? []).filter(entry =>
    entry.operationDock && (!entry.operationDock.desktopOnly || mobile === false) && pluginUiMayMount(entry)),
  [listing.data, mobile]);
  const latest = useRef(entries);
  latest.current = entries;
  const load = useCallback(async (entry: PluginUiListing): Promise<LoadedPluginDockSource | null> => {
    const registration = await loadPluginUi(entry.name, entry.url, entry.cssUrl);
    if (!registration || !pluginUiMayMount(entry, registration)) return null;
    if (!registration.operationDock) throw new Error('Declared plugin dock source is not registered');
    return { plugin: entry.name, locale, strings: entry.strings ?? {},
      desktopOnly: entry.operationDock!.desktopOnly, source: registration.operationDock };
  }, [locale]);
  const queries = useQueries({ queries: entries.map(entry => ({
    queryKey: ['plugin-dock-source', entry.name, entry.url, entry.cssUrl ?? null, locale],
    queryFn: () => load(entry), staleTime: Infinity,
  })) });
  return {
    sources: queries.flatMap(query => query.data ? [query.data] : []),
    resolveSource: async (plugin: string) => {
      const entry = latest.current.find(entry => entry.name === plugin);
      if (!entry) throw new Error('Plugin dock source is unavailable');
      const loaded = await load(entry);
      if (!loaded) throw new Error('Plugin dock source could not be loaded');
      return loaded;
    },
  };
}
